import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useToast } from '@kanos/ui';
import { useQueryClient } from '@tanstack/react-query';
import { CloudOff, WifiOff } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { api } from './api.js';
import { useConnection } from './connection.js';
/** While Kanos does not answer, it is asked this often - no page may be asking right now. */
const PROBE_MS = 15_000;
const TEXT = {
    offline: {
        icon: WifiOff,
        title: 'Keine Internetverbindung.',
        detail: 'Ihre Eingaben auf dieser Seite bleiben erhalten.',
    },
    unreachable: {
        icon: CloudOff,
        title: 'Kanos ist gerade nicht erreichbar.',
        detail: 'Kanos versucht es weiter; Ihre Eingaben auf dieser Seite bleiben erhalten.',
    },
};
/**
 * Says when the app cannot reach Kanos - without a network, or with Kanos not answering - at
 * the top of every page and to screen readers, never by colour alone. Once the connection is
 * back it says so briefly and loads what failed meanwhile.
 */
export function ConnectionNotice() {
    const state = useConnection();
    const toast = useToast();
    const queryClient = useQueryClient();
    const previous = useRef(state);
    useEffect(() => {
        if (previous.current !== 'online' && state === 'online') {
            toast({ tone: 'success', title: 'Verbindung wieder da' });
            void queryClient.refetchQueries({
                type: 'active',
                predicate: (q) => q.state.status === 'error',
            });
        }
        previous.current = state;
    }, [state, toast, queryClient]);
    useEffect(() => {
        if (state !== 'unreachable')
            return undefined;
        // Any answer will do: even "not signed in" says Kanos is back (`watchedFetch`).
        const timer = setInterval(() => {
            void api.GET('/api/v1/me/session').catch(() => undefined);
        }, PROBE_MS);
        return () => clearInterval(timer);
    }, [state]);
    const text = state === 'online' ? null : TEXT[state];
    return (_jsx("div", { role: "status", "aria-live": "polite", className: "pointer-events-none fixed inset-x-0 top-[max(0.75rem,env(safe-area-inset-top))] z-[60] flex justify-center px-safe-4", children: text && (_jsxs("p", { className: "glass pointer-events-auto flex max-w-xl items-start gap-2.5 rounded-lg bg-warning-bg px-4 py-3 text-sm text-warning-fg ring-1 ring-hairline", children: [_jsx(text.icon, { "aria-hidden": true, className: "mt-0.5 size-4 shrink-0" }), _jsxs("span", { children: [_jsx("span", { className: "font-semibold", children: text.title }), " ", text.detail] })] })) }));
}
//# sourceMappingURL=connection-notice.js.map